/**
 * 调试工具栏组件
 *
 * 提供工作流执行调试控制功能
 * 包含：开始、暂停/恢复、单步执行、中断等控制按钮
 * 参考: docs/ragflow_web 工作流设计
 */

import React, { memo, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { useExecutionStore } from '../../store/workflowStore';
import { ExecutionState } from '../../types/workflow';

// ==================== 类型定义 ====================

export interface DebugToolbarProps {
  /** 自定义类名 */
  className?: string;
  /** 是否显示快捷键提示 */
  showShortcutHints?: boolean;
  /** 是否垂直布局 */
  vertical?: boolean;
  /** 是否紧凑模式 */
  compact?: boolean;
  /** 开始执行回调 */
  onStart?: () => void;
  /** 暂停执行回调 */
  onPause?: () => void;
  /** 恢复执行回调 */
  onResume?: () => void;
  /** 单步执行回调 */
  onStep?: () => void;
  /** 中断执行回调 */
  onInterrupt?: () => void;
  /** 重新执行回调 */
  onRestart?: () => void;
}

// ==================== 状态配置 ====================

interface StateConfig {
  label: string;
  bgClass: string;
  textClass: string;
  icon?: React.ReactNode;
}

const EXECUTION_STATE_CONFIG: Record<ExecutionState, StateConfig> = {
  [ExecutionState.IDLE]: {
    label: '空闲',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-600',
  },
  [ExecutionState.RUNNING]: {
    label: '执行中',
    bgClass: 'bg-paper-100',
    textClass: 'text-paper-800',
    icon: (
      <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  [ExecutionState.PAUSED]: {
    label: '已暂停',
    bgClass: 'bg-warning-100',
    textClass: 'text-warning-700',
  },
  [ExecutionState.STEP_WAITING]: {
    label: '单步等待',
    bgClass: 'bg-paper-100',
    textClass: 'text-paper-800',
  },
  [ExecutionState.COMPLETED]: {
    label: '已完成',
    bgClass: 'bg-success-100',
    textClass: 'text-success-700',
  },
  [ExecutionState.FAILED]: {
    label: '失败',
    bgClass: 'bg-error-100',
    textClass: 'text-error-700',
  },
  [ExecutionState.INTERRUPTED]: {
    label: '已中断',
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-600',
  },
};

// ==================== 按钮组件 ====================

interface ControlButtonProps {
  /** 按钮图标 */
  icon: React.ReactNode;
  /** 点击回调 */
  onClick: () => void;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否激活状态 */
  active?: boolean;
  /** 提示文本 */
  title: string;
  /** 快捷键提示 */
  shortcut?: string;
  /** 显示快捷键提示 */
  showShortcut?: boolean;
  /** 按钮样式变体 */
  variant?: 'default' | 'primary' | 'danger' | 'success';
  /** 是否紧凑模式 */
  compact?: boolean;
}

const ControlButton = memo<ControlButtonProps>(({
  icon,
  onClick,
  disabled = false,
  active = false,
  title,
  shortcut,
  showShortcut = false,
  variant = 'default',
  compact = false,
}) => {
  const variantClasses = {
    default: 'bg-white hover:bg-gray-50 text-gray-700 border-gray-200',
    primary: 'bg-ink-800 hover:bg-ink-700 text-white border-paper-500',
    danger: 'bg-white hover:bg-error-50 text-error-600 border-error-200',
    success: 'bg-white hover:bg-success-50 text-success-600 border-success-200',
  };

  const disabledClasses = 'opacity-50 cursor-not-allowed bg-gray-50 text-gray-400';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={shortcut ? `${title} (${shortcut})` : title}
      className={clsx(
        'inline-flex items-center justify-center gap-1',
        'border rounded-lg font-medium',
        'transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-paper-500 focus:ring-offset-1',
        compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm',
        disabled ? disabledClasses : variantClasses[variant],
        active && !disabled && 'ring-2 ring-paper-500 ring-offset-1',
        !disabled && 'hover:shadow-sm active:scale-[0.98]',
      )}
    >
      {icon}
      {showShortcut && shortcut && (
        <span className="text-gray-400 ml-1 text-xs">
          {shortcut}
        </span>
      )}
    </button>
  );
});

ControlButton.displayName = 'ControlButton';

// ==================== 状态徽章组件 ====================

const ExecutionStateBadge = memo<{ state: ExecutionState }>(({ state }) => {
  const config = EXECUTION_STATE_CONFIG[state] || EXECUTION_STATE_CONFIG[ExecutionState.IDLE];

  return (
    <span className={clsx(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
      config.bgClass,
      config.textClass,
    )}>
      {config.icon}
      {config.label}
    </span>
  );
});

ExecutionStateBadge.displayName = 'ExecutionStateBadge';

// ==================== 主组件 ====================

export const DebugToolbar = memo<DebugToolbarProps>(({
  className,
  showShortcutHints = true,
  vertical = false,
  compact = false,
  onStart,
  onPause,
  onResume,
  onStep,
  onInterrupt,
  onRestart,
}) => {
  // 从 store 获取执行状态
  const executionState = useExecutionStore((state) => state.executionState);
  const setExecutionState = useExecutionStore((state) => state.setExecutionState);
  const resetExecution = useExecutionStore((state) => state.resetExecution);
  const setStepMode = useExecutionStore((state) => state.setStepMode);
  const setError = useExecutionStore((state) => state.setError);
  const isStepMode = useExecutionStore((state) => state.isStepMode);

  // ==================== 计算按钮可用状态 ====================

  const buttonStates = useMemo(() => ({
    // 可以开始执行：空闲、已完成、失败、中断状态
    canStart: [
      ExecutionState.IDLE,
      ExecutionState.COMPLETED,
      ExecutionState.FAILED,
      ExecutionState.INTERRUPTED,
    ].includes(executionState),
    
    // 可以暂停：运行中状态
    canPause: executionState === ExecutionState.RUNNING,
    
    // 可以恢复：暂停状态
    canResume: executionState === ExecutionState.PAUSED,
    
    // 可以单步执行：空闲、单步等待、暂停状态
    canStep: [
      ExecutionState.IDLE,
      ExecutionState.STEP_WAITING,
      ExecutionState.PAUSED,
      ExecutionState.COMPLETED,
      ExecutionState.FAILED,
    ].includes(executionState),
    
    // 可以中断：运行中、暂停、单步等待状态
    canInterrupt: [
      ExecutionState.RUNNING,
      ExecutionState.PAUSED,
      ExecutionState.STEP_WAITING,
    ].includes(executionState),
    
    // 可以重新执行：已完成、失败、中断状态
    canRestart: [
      ExecutionState.COMPLETED,
      ExecutionState.FAILED,
      ExecutionState.INTERRUPTED,
    ].includes(executionState),
  }), [executionState]);

  // ==================== 控制方法 ====================

  // 开始执行
  const handleStart = useCallback(() => {
    if (!buttonStates.canStart) return;
    
    // 重置执行状态
    resetExecution();
    setStepMode(false);
    setError(null);
    setExecutionState(ExecutionState.RUNNING);
    
    onStart?.();
  }, [buttonStates.canStart, resetExecution, setStepMode, setError, setExecutionState, onStart]);

  // 暂停执行
  const handlePause = useCallback(() => {
    if (!buttonStates.canPause) return;
    
    setExecutionState(ExecutionState.PAUSED);
    onPause?.();
  }, [buttonStates.canPause, setExecutionState, onPause]);

  // 恢复执行
  const handleResume = useCallback(() => {
    if (!buttonStates.canResume) return;
    
    setStepMode(false);
    setExecutionState(ExecutionState.RUNNING);
    onResume?.();
  }, [buttonStates.canResume, setStepMode, setExecutionState, onResume]);

  // 切换暂停/恢复
  const handleTogglePause = useCallback(() => {
    if (buttonStates.canPause) {
      handlePause();
    } else if (buttonStates.canResume) {
      handleResume();
    }
  }, [buttonStates.canPause, buttonStates.canResume, handlePause, handleResume]);

  // 单步执行
  const handleStep = useCallback(() => {
    if (!buttonStates.canStep) return;
    
    if (executionState === ExecutionState.STEP_WAITING) {
      // 继续执行下一步
      setExecutionState(ExecutionState.RUNNING);
    } else if (executionState === ExecutionState.PAUSED) {
      // 从暂停恢复为单步模式
      setStepMode(true);
      setExecutionState(ExecutionState.RUNNING);
    } else {
      // 开始单步模式
      resetExecution();
      setStepMode(true);
      setError(null);
      setExecutionState(ExecutionState.RUNNING);
    }
    
    onStep?.();
  }, [buttonStates.canStep, executionState, resetExecution, setStepMode, setError, setExecutionState, onStep]);

  // 中断执行
  const handleInterrupt = useCallback(() => {
    if (!buttonStates.canInterrupt) return;
    
    setExecutionState(ExecutionState.INTERRUPTED);
    onInterrupt?.();
  }, [buttonStates.canInterrupt, setExecutionState, onInterrupt]);

  // 重新执行
  const handleRestart = useCallback(() => {
    if (!buttonStates.canRestart) return;
    
    resetExecution();
    setStepMode(false);
    setError(null);
    setExecutionState(ExecutionState.RUNNING);
    
    onRestart?.();
  }, [buttonStates.canRestart, resetExecution, setStepMode, setError, setExecutionState, onRestart]);

  // ==================== 渲染 ====================

  return (
    <div className={clsx(
      'inline-flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg shadow-sm',
      vertical && 'flex-col',
      className,
    )}>
      {/* 状态徽章 */}
      <div className="flex items-center gap-2 px-2">
        <ExecutionStateBadge state={executionState} />
        {isStepMode && executionState !== ExecutionState.IDLE && (
          <span className="text-xs text-paper-700 font-medium">
            [单步模式]
          </span>
        )}
      </div>

      {/* 分隔线 */}
      <div className={clsx('bg-gray-200', vertical ? 'w-full h-px' : 'w-px h-6')} />

      {/* 控制按钮组 */}
      <div className={clsx('flex items-center gap-1.5', vertical && 'flex-col')}>
        {/* 开始执行按钮 */}
        {buttonStates.canStart && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            }
            onClick={handleStart}
            title="开始执行"
            shortcut="F5"
            showShortcut={showShortcutHints}
            variant="primary"
            compact={compact}
          />
        )}

        {/* 暂停按钮 */}
        {buttonStates.canPause && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            }
            onClick={handleTogglePause}
            title="暂停"
            shortcut="Space"
            showShortcut={showShortcutHints}
            compact={compact}
          />
        )}

        {/* 恢复按钮 */}
        {buttonStates.canResume && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            }
            onClick={handleTogglePause}
            title="恢复"
            shortcut="Space"
            showShortcut={showShortcutHints}
            variant="success"
            compact={compact}
          />
        )}

        {/* 单步执行按钮 */}
        {buttonStates.canStep && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            }
            onClick={handleStep}
            title={executionState === ExecutionState.STEP_WAITING ? '下一步' : '单步执行'}
            shortcut="F10"
            showShortcut={showShortcutHints}
            active={isStepMode && executionState !== ExecutionState.IDLE}
            compact={compact}
          />
        )}

        {/* 中断按钮 */}
        {buttonStates.canInterrupt && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
              </svg>
            }
            onClick={handleInterrupt}
            title="中断执行"
            shortcut="Esc"
            showShortcut={showShortcutHints}
            variant="danger"
            compact={compact}
          />
        )}

        {/* 重新执行按钮 */}
        {buttonStates.canRestart && (
          <ControlButton
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
            onClick={handleRestart}
            title="重新执行"
            shortcut="F5"
            showShortcut={showShortcutHints}
            compact={compact}
          />
        )}
      </div>
    </div>
  );
});

DebugToolbar.displayName = 'DebugToolbar';

export default DebugToolbar;
